import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import config from '../../config/config';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Eye, EyeOff, User, Lock, Shield } from 'lucide-react';
import { Modal, Button } from 'react-bootstrap';
import { FaTimesCircle, FaUser, FaLock, FaEye, FaEyeSlash, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import ParticlesBackground from '../../components/common/ParticlesBackground';
import '../../styles/login.css';

const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [focusedField, setFocusedField] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [modalType, setModalType] = useState('error');
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                const currentTime = Date.now() / 1000;

                if (decodedToken.exp > currentTime) {
                    let targetPath = '/';
                    if (decodedToken.role === 1 || decodedToken.role === 'kl') {
                        targetPath = '/kl';
                    } else if (decodedToken.role === 0 || decodedToken.role === 2) {
                        targetPath = '/admin';
                    } else if (decodedToken.role === 3) {
                        targetPath = '/publikator';
                    }
                    navigate(targetPath, { replace: true });
                } else {
                    localStorage.removeItem('token');
                }
            } catch (error) {
                console.error("Token tidak valid atau rusak:", error);
                localStorage.removeItem('token');
            }
        }
    }, [navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setMessage('');

        if (!username || !password) {
            showNotification('Username dan Password tidak boleh kosong', 'warning');
            return;
        }

        if (!executeRecaptcha) {
            showNotification('reCAPTCHA belum dimuat. Silakan refresh halaman.', 'error');
            return;
        }

        setLoading(true);

        try {
            const recaptchaToken = await executeRecaptcha('login');
            const response = await axios.post(`${config.API_URL}/login`, {
                username,
                password,
                recaptchaToken,
            });

            if (response.status === 200) {
                setShowSuccessModal(true);
                localStorage.setItem('token', response.data.token);
                setTimeout(() => {
                    setShowSuccessModal(false);
                    navigate(response.data.redirectUrl);
                }, 2000);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Gagal terhubung ke server';
            showNotification(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (message, type = 'error') => {
        setMessage(message);
        setModalType(type);
        setShowModal(true);
    };

    const getModalIcon = () => {
        switch (modalType) {
            case 'success': return <FaCheckCircle size={50} className="modal-icon success-icon" />;
            case 'warning': return <FaExclamationTriangle size={50} className="modal-icon warning-icon" />;
            default: return <FaTimesCircle size={50} className="modal-icon error-icon" />;
        }
    };

    const getModalClass = () => `modern-modal ${modalType}-modal`;

    return (
        <>
            <div className="login-container">
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 0,
                    pointerEvents: 'none'
                }}>
                    <ParticlesBackground color="rgba(42, 82, 152, 0.3)" />
                </div>

                <div className="login-logo">
                    <a href="https://onemap.big.go.id" rel="noopener noreferrer">
                        <img src="/ksp.png" alt="Logo" />
                    </a>
                </div>
                <div className="login-form-container">
                    <div className="login-card modern-login-card">
                        <div className="login-header">
                            <div className="modern-logo-container"><img src="/logo.png" alt="Logo" /></div>
                            <h3>Selamat Datang</h3>
                            <p>Silakan masuk untuk melanjutkan</p>
                        </div>
                        <form onSubmit={handleLogin}>
                            <div className="modern-input-group">
                                <label className={`modern-label ${focusedField === 'username' || username ? 'active' : ''}`}>Username</label>
                                <div className="modern-input-wrapper">
                                    <FaUser className={`input-icon ${focusedField === 'username' ? 'active' : ''}`} />
                                    <input type="text" className="form-control modern-input-field" value={username} onChange={(e) => setUsername(e.target.value)} onFocus={() => setFocusedField('username')} onBlur={() => setFocusedField('')} placeholder={focusedField === 'username' || username ? "Masukkan username" : ""} autoComplete="username" />
                                </div>
                            </div>
                            <div className="modern-input-group">
                                <label className={`modern-label ${focusedField === 'password' || password ? 'active' : ''}`}>Password</label>
                                <div className="modern-input-wrapper">
                                    <FaLock className={`input-icon ${focusedField === 'password' ? 'active' : ''}`} />
                                    <input type={showPassword ? "text" : "password"} className="form-control modern-input-field" value={password} onChange={(e) => setPassword(e.target.value)} onFocus={() => setFocusedField('password')} onBlur={() => setFocusedField('')} placeholder={focusedField === 'password' || password ? "Masukkan password" : ""} autoComplete="current-password" />
                                    <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <FaEyeSlash /> : <FaEye />}</button>
                                </div>
                            </div>
                            <button type="submit" className="btn btn-primary w-100 modern-login-btn" disabled={loading}>
                                {loading && (<div className="loading-overlay"><span className="spinner-border spinner-border-sm loading-spinner"></span><span className="loading-text">Memproses...</span></div>)}
                                <span className={loading ? 'invisible' : 'visible'}>Masuk</span>
                            </button>
                            <div className="login-footer-links">
                                <p className="register-link">
                                    Belum punya akun? <a href="/register">Daftar di sini</a>
                                </p>
                                <p className="home-link">
                                    <a href="/">← Kembali ke Beranda</a>
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Modal untuk Notifikasi Error/Warning */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered className={getModalClass()} backdrop="static">
                <Modal.Body className="modern-modal-body">
                    <div className="modal-content-wrapper">{getModalIcon()}<div className="modal-message"><h5 className="modal-title">{modalType === 'warning' ? 'Perhatian!' : 'Oops!'}</h5><p className="modal-text">{message}</p></div></div>
                    <div className="modal-actions"><Button className={`modern-modal-btn ${modalType}-btn`} onClick={() => setShowModal(false)}>Mengerti</Button></div>
                </Modal.Body>
            </Modal>

            {/* Modal untuk Notifikasi Sukses dengan Smooth Animation */}
            <Modal show={showSuccessModal} centered className="success-popup-modal" backdrop="static" keyboard={false}>
                <Modal.Body className="success-modal-body">
                    <div className="success-animation">
                        <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ 
                                type: "spring",
                                stiffness: 260,
                                damping: 20,
                                duration: 0.6
                            }}
                            className="success-icon-wrapper"
                        >
                            <FaCheckCircle size={80} className="success-icon-animated" />
                        </motion.div>
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.5 }}
                            className="success-content"
                        >
                            <h4 className="success-title">Login Berhasil!</h4>
                            <p className="success-subtitle">Mengalihkan ke dashboard...</p>
                            <div className="loading-dots">
                                <motion.span 
                                    className="dot"
                                    animate={{ y: [0, -10, 0] }}
                                    transition={{ repeat: Infinity, duration: 0.6, delay: 0 }}
                                />
                                <motion.span 
                                    className="dot"
                                    animate={{ y: [0, -10, 0] }}
                                    transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }}
                                />
                                <motion.span 
                                    className="dot"
                                    animate={{ y: [0, -10, 0] }}
                                    transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }}
                                />
                            </div>
                        </motion.div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default LoginPage;